import { useEffect, useRef, useState } from 'react';

// Drives the count-up animation on a single KPI tile.
//
// Fires on every Dashboard mount — navigating away to another page
// and back replays the animation. The hook's hasRunRef is scoped to
// the hook instance, so when React unmounts/remounts the Dashboard
// (route change) the ref resets and the animation runs again.
//
// Counts from 0 to the live target value over 750ms with an
// ease-out-cubic curve. Reduced-motion users skip the animation
// entirely — display jumps to the target on first paint.
//
// Returns a `display` integer the consumer renders + a `done` boolean
// flipped when the count-up finishes (used by the dashboard to fade
// in the delta arrow after a 200ms beat).

const DURATION_MS = 750;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useKpiCountUp(target, options = {}) {
  const { enabled = true } = options;
  const safeTarget = Number.isFinite(target) ? Math.max(0, Math.round(target)) : 0;
  // Initial display is 0 (not safeTarget) so the very first paint
  // shows zero — the animation then sweeps up to the real value once
  // data lands. Without this, target=0 → target=real on first stats
  // fetch would briefly show the real value before the animation
  // resets it back to 0.
  const [display, setDisplay] = useState(0);
  const [done, setDone] = useState(true);
  const rafRef = useRef(null);
  const hasRunRef = useRef(false);

  // Single effect handles both the initial 0 → target animation and
  // subsequent live-data snaps. Previously this lived in a second
  // useEffect which would race the main one — when `safeTarget`
  // changed at the same render as `enabled` flipping true, the snap
  // effect ran AFTER the main effect on that render, saw
  // hasRunRef.current = true (just set), and instantly overwrote
  // display = safeTarget. That killed the animation. Folding both
  // paths into one effect with [enabled, safeTarget] deps removes
  // the race: each render runs exactly one effect body that knows
  // which path to take.
  useEffect(() => {
    if (!enabled) return undefined;
    if (hasRunRef.current) {
      // Real-time update after the initial animation has settled.
      // Snap silently to the new value.
      setDisplay(safeTarget);
      return undefined;
    }
    if (prefersReducedMotion()) {
      hasRunRef.current = true;
      setDisplay(safeTarget);
      setDone(true);
      return undefined;
    }
    hasRunRef.current = true;
    setDisplay(0);
    setDone(false);
    const startedAt = performance.now();
    const tick = (now) => {
      const elapsed = now - startedAt;
      const t = Math.min(elapsed / DURATION_MS, 1);
      const eased = easeOutCubic(t);
      setDisplay(Math.round(safeTarget * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(safeTarget);
        setDone(true);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, safeTarget]);

  return { display, done };
}
